import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { isEmpty } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TaskWorkspaceStore } from '../../../../../core/h3ml/state/task-workspace-store';
import { DebouncedEventTracker } from '../../../../../cross-team-shared/event-tracking/debounced-event-tracker';
import { EventTrackingValueWithSource } from '../../../../../cross-team-shared/misc-utils';
import { isPointCloudOutput } from '../../../../../cross-team-shared/misc-utils/output-utils';
import { loadAssetBoundingBoxForOutput } from '../../../../../cross-team-shared/misc-utils/task-data-utils';
import {
  defaultHeightRange,
  defaultIntensityRange,
  PointColorMode,
} from '../../../../../cross-team-shared/state/layer-settings-state-3d';
import { settingsStoreToken } from '../../../../../cross-team-shared/state/setting-state';
import { EventTrackingService } from '../../../../../shared/event-tracking/event-tracking.service';
import {
  LidarSensor,
  MultiPointCloud,
  PointCloudOutput,
} from '../../../../../team-apis/otterdam/outputs/point-cloud-output';
import { SliderSettingConfig } from '../settings-panel/slider-settings-config';

interface PointColorModeOption {
  mode: PointColorMode;
  label: string;
}

@Component({
  selector: 'h3-point-color-settings',
  templateUrl: './point-color-settings.component.html',
  styleUrl: './point-color-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PointColorSettingsComponent extends DirectiveSuperclass {
  private readonly settingsStore =
    inject(settingsStoreToken)('layerSettings3d');
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);

  PointColorMode = PointColorMode;
  pointColorModeOptions$: Observable<PointColorModeOption[]>;

  defaultIntensityRange = defaultIntensityRange;
  defaultHeightFromLocation = defaultHeightRange;
  intensityRangeConfig = {
    ...defaultIntensityRange,
    step: 0.001,
  };
  heightFromLocationConfig: SliderSettingConfig;

  intensityRangeStore = this.settingsStore('intensityRange');
  pointColorModeStore = this.settingsStore('pointColorMode');
  heightRangeStore = this.settingsStore('heightRange');
  sensors$: Observable<LidarSensor[]>;
  sensorSelectionTracker = new DebouncedEventTracker(
    { id: 'sensor_selection_change', bucket: 'frame' },
    this.eventTrackingService,
  );

  constructor() {
    super();
    const minMaxHeightFromLocation = this.getMinMaxHeightFromLocation();
    this.heightFromLocationConfig = {
      ...minMaxHeightFromLocation,
      step: 0.1,
    };

    const multiPointCloud$ =
      this.getOutputStore()('meta')('multi_point_cloud').$;
    this.pointColorModeOptions$ = multiPointCloud$.pipe(
      map((multiPointCloud) => this.getColorModeOptions(multiPointCloud)),
    );
    this.sensors$ = multiPointCloud$.pipe(
      map((multiPointCloud) => multiPointCloud?.sensors ?? []),
    );
  }

  getMinMaxHeightFromLocation() {
    const { project, task } = this.taskWorkspaceStore.state();
    const output = project.outputs.find(isPointCloudOutput);
    const defaultDistance = 100;
    assert(output, 'No point cloud output found');

    const { min, max } = loadAssetBoundingBoxForOutput(output.name, task) || {
      min: [0, 0, 0],
      max: [defaultDistance, defaultDistance, defaultHeightRange.max],
    };

    return {
      min: 0,
      max: Math.max(Math.abs(max[2]), Math.abs(min[2])),
    };
  }

  updatePointColorMode(
    mode: PointColorMode,
    eventTrackingValueWithSource: EventTrackingValueWithSource,
  ) {
    this.pointColorModeStore.set(mode);

    this.eventTrackingService.track({
      id: 'change_point_color_mode',
      bucket: 'annotation',
      new_value: getOptionForPointColorMode(this.pointColorModeStore.state())
        .label,
      ...eventTrackingValueWithSource,
    });
  }

  private getColorModeOptions(multiPointCloud?: MultiPointCloud) {
    const pointColorModeOptions = [
      getOptionForPointColorMode(PointColorMode.Intensity),
      getOptionForPointColorMode(PointColorMode.InsideFocusedCuboid),
      getOptionForPointColorMode(PointColorMode.Height),
    ];

    if (multiPointCloud?.enabled && !isEmpty(multiPointCloud.sensors)) {
      pointColorModeOptions.push(
        getOptionForPointColorMode(PointColorMode.Sensor),
      );
    }

    return pointColorModeOptions;
  }

  private getOutputStore(): Store<PointCloudOutput> {
    const { project } = this.taskWorkspaceStore.state();
    const pointCloudOutputIndex = project.outputs.findIndex(isPointCloudOutput);
    return this.taskWorkspaceStore('project')('outputs')(pointCloudOutputIndex);
  }
}

function getOptionForPointColorMode(
  mode: PointColorMode,
): PointColorModeOption {
  const pointColorModeMaterials: StrictRecord<PointColorMode, string> = {
    [PointColorMode.Intensity]: 'intensity',
    [PointColorMode.Sensor]: 'sensor',
    [PointColorMode.InsideFocusedCuboid]: 'inside focus cuboid',
    [PointColorMode.Height]: 'height',
  };
  const label = pointColorModeMaterials[mode];
  assert(label, `Unknown point color mode: ${mode}`);
  return { mode, label };
}
